import React, { useState } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Chip, 
    Avatar, 
    IconButton,
    AvatarGroup,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../theme';
import { formattedDate, isEmpty } from '../Utils/Functions';
// Removed MoreHorizIcon import as we're using CardMenuActions instead
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Get initials from a name
 * @param {string} name - Full name
 * @returns {string} - Initials (up to 2 characters)
 */
const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length > 1) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return (name.substring(0, 2)).toUpperCase();
};

/**
 * PosteCard Component - A reusable card component for displaying poste information
 * @param {Object} props
 * @param {Object} props.poste - The poste data to display
 * @param {number} props.index - Index for color rotation
 * @param {Function} props.onView - Callback when view button is clicked
 * @param {Function} props.onEdit - Callback when edit button is clicked
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {Function} props.onManageUsers - Callback when manage users button is clicked
 * @param {Function} props.onManageSkills - Callback when manage skills button is clicked
 */
const PosteCard = ({ 
    poste, 
    index = 0, 
    onView, 
    onEdit, 
    onDelete, 
    onManageUsers, 
    onManageSkills,
    ViewComponent,
    EditComponent,
    DeleteComponent,
    ManageUsersComponent,
    ManageSkillsComponent,
    menuItems = []
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    // Removed menu state since we're not using the built-in menu anymore
    
    // State for dialog visibility
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [manageUsersDialogOpen, setManageUsersDialogOpen] = useState(false);
    const [manageSkillsDialogOpen, setManageSkillsDialogOpen] = useState(false);

    // Removed menu handlers since we're not using the built-in menu anymore
    
    // Dialog open/close handlers - removed menu close references since menu is gone
    const handleViewOpen = () => {
        if (onView) onView(poste);
        if (ViewComponent) setViewDialogOpen(true);
    };
    
    const handleEditOpen = () => {
        if (onEdit) onEdit(poste);
        if (EditComponent) setEditDialogOpen(true);
    };
    
    const handleDeleteOpen = () => {
        if (onDelete) onDelete(poste);
        if (DeleteComponent) setDeleteDialogOpen(true);
    };
    
    const handleManageUsersOpen = () => {
        if (onManageUsers) onManageUsers(poste);
        if (ManageUsersComponent) setManageUsersDialogOpen(true);
    };
    
    const handleManageSkillsOpen = () => {
        if (onManageSkills) onManageSkills(poste);
        if (ManageSkillsComponent) setManageSkillsDialogOpen(true);
    };
    
    // Close handlers for dialogs
    const handleViewClose = () => setViewDialogOpen(false);
    const handleEditClose = () => setEditDialogOpen(false);
    const handleDeleteClose = () => setDeleteDialogOpen(false);
    const handleManageUsersClose = () => setManageUsersDialogOpen(false);
    const handleManageSkillsClose = () => setManageSkillsDialogOpen(false);

    // Card color rotation based on index
    const getCardColor = (idx) => {
        const colorOptions = [
            { bg: 'rgba(255, 179, 128, 0.3)', border: colors.Orange[200] },
            { bg: 'rgba(144, 238, 144, 0.3)', border: colors.green[200] },
            { bg: 'rgba(216, 191, 216, 0.3)', border: colors.purple[200] },
            { bg: 'rgba(173, 216, 230, 0.3)', border: colors.Blue[200] },
            { bg: 'rgba(255, 182, 193, 0.3)', border: colors.Pink[200] },
            { bg: 'rgba(224, 224, 224, 0.3)', border: colors.grey[200] },
            { bg: colors.purple[100], border: colors.purple[200] },
            { bg: colors.Pink[100], border: colors.Pink[200] },
            { bg: colors.indigo[100], border: colors.indigo[200] },
            { bg: colors.brown[100], border: colors.brown[200] },
            { bg: colors.skyBlue[100], border: colors.skyBlue[200] },

            

        ];
        return colorOptions[idx % colorOptions.length];
    };

    const cardColor = getCardColor(index);
    
    // Determine if poste is management or operational based on name (example logic)
    const isManagement = poste.name?.toLowerCase().includes('manager') || poste.name?.toLowerCase().includes('director');
    
    return (
        <Card 
            sx={{
                backgroundColor: cardColor.bg,
                borderColor: cardColor.border,
                borderWidth: 2,
                borderStyle: 'solid',
                borderRadius: '16px',
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)'
                }
            }}
        >
            <CardContent>
                {/* Header with date and actions */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="body2" fontWeight="medium" color="text.secondary">
                        {formattedDate(new Date(poste.createdAt))}
                    </Typography>
                    <Box display="flex" alignItems="center">
                        <IconButton 
                            size="small" 
                            sx={{ 
                                //'&:hover': { bgcolor: 'rgba(255,255,255,0.5)' },
                                position: 'absolute',
                                top: '13px', // Aligned with the menu dots at 12px from top
                                right: '46px' // Positioned to the left of where the menu dots will be
                            }}
                        >
                            <BookmarkBorderIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {/* Logo and title */}
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <Avatar
                        sx={{
                            width: 48,
                            height: 48,
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            boxShadow: 1,
                            border: '2px solid white'
                        }}
                    >
                        {getInitials(poste.name)}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" component="h3" fontWeight="bold" sx={{
                            mb: 0.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {poste.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {poste.organization || ''}
                        </Typography>
                    </Box>
                </Box>

                {/* Tags */}
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                   
                    <Chip
                        label={`${poste.users?.length || 0} user(s)`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`${poste.skillRequirements?.length || 0} skill(s)`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: 'text.secondary' }}
                    />
                    <Chip
                        label={`Updated: ${formattedDate(new Date(poste.updatedAt))}`}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: 'text.secondary' }}
                    />
                </Box>

                {/* Users */}
                {Array.isArray(poste.users) && poste.users.length > 0 && (
                    <Box mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AvatarGroup max={4} sx={{
                            '& .MuiAvatar-root': { width: 30, height: 30, fontSize: '0.75rem', border: '2px solid white' }
                        }}>
                            {poste.users.map((user, idx) => (
                                <Avatar key={user.id || idx} alt={user.username}>
                                    {getInitials(user.username)}
                                </Avatar>
                            ))}
                        </AvatarGroup>
                        
                        {poste.users.length <= 3 && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                {poste.users.map(u => u.username).join(', ')}
                            </Typography>
                        )}
                    </Box>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Footer - Created By */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            Created by
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Avatar 
                            sx={{ 
                                width: 24, 
                                height: 24, 
                                fontSize: '0.625rem', 
                                bgcolor: 'grey.600',
                                color: 'white'
                            }}
                        >
                            {getInitials(poste.createdBy?.username)}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                            {poste.createdBy?.username}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
            
            {/* Internal Dialogs */}
            {ViewComponent && (
                <Dialog 
                    open={viewDialogOpen} 
                    onClose={handleViewClose} 
                    maxWidth="md" 
                    fullWidth
                    aria-labelledby="view-dialog-title"
                >
                    <DialogTitle id="view-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center">
                            <InfoIcon sx={{ mr: 1 }} />
                            Post Information
                        </Box>
                        <IconButton aria-label="close" onClick={handleViewClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <ViewComponent row={{ original: poste }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleViewClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            )}
            
            {EditComponent && (
                <Dialog 
                    open={editDialogOpen} 
                    onClose={handleEditClose} 
                    maxWidth="md" 
                    fullWidth
                    aria-labelledby="edit-dialog-title"
                >
                    <DialogTitle id="edit-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center">
                            <EditIcon sx={{ mr: 1 }} />
                            Edit Post
                        </Box>
                        <IconButton aria-label="close" onClick={handleEditClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <EditComponent row={{ original: poste }} handleCloseDialog={handleEditClose} />
                    </DialogContent>
                </Dialog>
            )}
            
            {DeleteComponent && (
                <Dialog 
                    open={deleteDialogOpen} 
                    onClose={handleDeleteClose} 
                    maxWidth="sm" 
                    fullWidth
                    aria-labelledby="delete-dialog-title"
                >
                    <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center">
                            <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
                            Delete Post
                        </Box>
                        <IconButton aria-label="close" onClick={handleDeleteClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <DeleteComponent row={{ original: poste }} handleCloseDialog={handleDeleteClose} />
                    </DialogContent>
                </Dialog>
            )}
            
            {ManageUsersComponent && (
                <Dialog 
                    open={manageUsersDialogOpen} 
                    onClose={handleManageUsersClose} 
                    maxWidth="md" 
                    fullWidth
                    aria-labelledby="manage-users-dialog-title"
                >
                    <DialogTitle id="manage-users-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center">
                            <PeopleAltIcon sx={{ mr: 1 }} />
                            Manage Users
                        </Box>
                        <IconButton aria-label="close" onClick={handleManageUsersClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <ManageUsersComponent row={{ original: poste }} handleCloseDialog={handleManageUsersClose} />
                    </DialogContent>
                </Dialog>
            )}
            
            {ManageSkillsComponent && (
                <Dialog 
                    open={manageSkillsDialogOpen} 
                    onClose={handleManageSkillsClose} 
                    maxWidth="md" 
                    fullWidth
                    aria-labelledby="manage-skills-dialog-title"
                >
                    <DialogTitle id="manage-skills-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center">
                            <SettingsIcon sx={{ mr: 1 }} />
                            Manage Skills
                        </Box>
                        <IconButton aria-label="close" onClick={handleManageSkillsClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        <ManageSkillsComponent row={{ original: poste }} handleCloseDialog={handleManageSkillsClose} />
                    </DialogContent>
                </Dialog>
            )}
        </Card>
    );
};

export default PosteCard;
